import React from 'react'
import { X } from 'lucide-react'
import CustomDrawer from '@/shared/components/ui/Drawer/CustomDrawer'
import SecondaryButton from '@/shared/components/ui/Button/SecondaryButton'
import SquircleIcon from '@/shared/components/ui/SquircleIcon'
import { StatusChip } from '@/shared/components/ui/Status/StatusChip'
import { DRAWER_WIDTH } from './config'
import LabeledText from './LabeledText'
import {
    getAccountDomain,
    getAccountName,
    getInitials,
    getMatchScore,
    getMatchVariant,
    getSafeUrl,
} from './utils'

const CustomDrawerComponent = CustomDrawer as React.ComponentType<any>
const SecondaryButtonComponent = SecondaryButton as React.ComponentType<any>
const SquircleIconComponent = SquircleIcon as React.ComponentType<any>
const StatusChipComponent = StatusChip as React.ComponentType<any>

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <div className='flex flex-col gap-2'>
        <h4 className='text-base font-medium text-gray-800 dark:text-gray-100 font-sans'>{title}</h4>
        {children}
    </div>
)

export const SourceLink = ({ href, label }: { href: string | null; label: string }) =>
    href ? (
        <a
            href={href}
            target='_blank'
            rel='noopener noreferrer'
            className='text-xs font-normal text-gray-500 dark:text-gray-400 underline decoration-dotted font-sans'
        >
            {label}
        </a>
    ) : (
        <span className='text-xs font-normal text-gray-500 dark:text-gray-400 font-sans'>{label}</span>
    )

const defaultScoreLabel = (score: number) => `${score}% Match`

interface HeaderProps {
    account: any
    onClose: () => void
    scoreLabel: (score: number) => string
}

const Header = ({ account, onClose, scoreLabel }: HeaderProps) => {
    const name = getAccountName(account)
    const domain = getAccountDomain(account)
    const score = getMatchScore(account)
    return (
        <div className='flex p-4 items-center gap-3 border-b border-gray-200 dark:border-gray-800'>
            <SquircleIconComponent className='w-[38px] h-[38px]'>
                <span className='text-base font-medium text-white'>{getInitials(name)}</span>
            </SquircleIconComponent>
            <div className='flex flex-col gap-1 min-w-0'>
                <h3 className='text-base font-medium text-gray-800 dark:text-gray-100 font-sans truncate'>{name}</h3>
                {domain && <SourceLink href={getSafeUrl(domain)} label={domain} />}
            </div>
            {score > 0 && (
                <StatusChipComponent
                    variant={getMatchVariant(score)}
                    label={scoreLabel(score)}
                    className='px-3 py-1 text-xs font-medium self-start shrink-0'
                />
            )}
            <SecondaryButtonComponent
                title=''
                className='!w-8 !h-8 !min-w-8 !p-0 !gap-0 rounded-lg flex items-center ml-auto justify-center border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                icon={<X size={18} className='text-gray-500' strokeWidth={2.5} />}
                onClick={onClose}
            />
        </div>
    )
}

const WhyItMatched = ({ account }: { account: any }) => {
    const reason = String(account.reason || '').trim()
    const evidence = (Array.isArray(account.evidence) ? account.evidence : []).filter(
        (item: any) => item?.summary || item?.title,
    )
    return (
        <Section title='Why it matched'>
            <div className='-mx-4 flex flex-col gap-4'>
                {reason && <LabeledText text={reason} className='px-4 text-sm font-normal text-gray-600 dark:text-gray-300 font-sans' />}
                {evidence.map((item: any, index: number) => (
                    <span key={`${item?.source_url || item?.title}-${index}`} className='flex flex-col gap-2 px-4 border-b border-gray-200 dark:border-gray-700 last:border-b-0 border-dotted pb-4'>
                        {item?.summary && (
                            <LabeledText text={item.summary} className='text-sm font-normal text-gray-600 dark:text-gray-300 font-sans' />
                        )}
                        <SourceLink href={getSafeUrl(item?.source_url)} label={item?.title || 'Source'} />
                    </span>
                ))}
                {!reason && evidence.length === 0 && (
                    <p className='px-4 text-sm font-normal text-gray-500 dark:text-gray-400 font-sans'>
                        No supporting evidence is available yet.
                    </p>
                )}
            </div>
        </Section>
    )
}

interface AccountDetailsDrawerProps {
    account: any | null
    onClose: () => void
    /** Chip text for the score, e.g. "91% Match" or "82 Priority". */
    scoreLabel?: (score: number) => string
    /** Shown when the account has no overview; without it the Overview section is hidden. */
    emptyOverviewText?: string
    /** Action pinned to the bottom of the panel, e.g. "Add to accounts". */
    renderFooter?: (account: any) => React.ReactNode
}

/** Side panel for one company: identity, score, overview and why it surfaced (with sources). */
export default function AccountDetailsDrawer({
    account,
    onClose,
    scoreLabel = defaultScoreLabel,
    emptyOverviewText,
    renderFooter,
}: AccountDetailsDrawerProps) {
    const overview = account?.overview || emptyOverviewText
    return (
        <CustomDrawerComponent isOpen={Boolean(account)} onClose={onClose} width={DRAWER_WIDTH}>
            {account && (
                <div className='flex flex-col h-full'>
                    <Header account={account} onClose={onClose} scoreLabel={scoreLabel} />
                    {/* The body scrolls; the header and footer stay put. */}
                    <div className='flex flex-1 min-h-0 flex-col gap-4 p-4 overflow-y-auto'>
                        {overview && (
                            <Section title='Overview'>
                                <p className='text-sm font-normal text-gray-500 dark:text-gray-400 font-sans'>{overview}</p>
                            </Section>
                        )}
                        <WhyItMatched account={account} />
                    </div>
                    {renderFooter && (
                        <div className='shrink-0 border-t border-gray-200 dark:border-gray-800 p-4'>
                            {renderFooter(account)}
                        </div>
                    )}
                </div>
            )}
        </CustomDrawerComponent>
    )
}
